import { Navigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle, Button } from '@design-system';
import { ConsumerFlowLayout } from './components/consumer-flow-layout';
import { ConsumerLoadingState } from './components/consumer-loading-state';
import { useConsumerClient } from './hooks/use-consumer-client';
import { useConsumerPlan } from './hooks/use-consumer-plan';
import { usePolicyReviewInProgress } from './hooks/use-policy-review';
import { useRememberStep } from './hooks/use-remember-step';
import { useStepQuotes } from './hooks/use-step-quotes';
import { currentConsumerStep, isConsumerStep, type ConsumerStepId } from './lib/steps';
import { consumerEntryPath, consumerStepPath } from './lib/paths';
import { CONSUMER_STEP_COMPONENTS } from './steps';

interface ConsumerFlowPageProps {
  clientId: string;
  step: string;
}

function LoadError({ title, onRetry }: { title: string; onRetry: () => void }) {
  return (
    <Alert variant="destructive">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        Please try again in a moment.
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface ConsumerStepContentProps {
  clientId: string;
  step: ConsumerStepId;
  isLoading: boolean;
  plan: { isError: boolean; retry: () => void };
  quotes: { hasFailed: boolean; retry: () => void };
}

function ConsumerStepContent({ clientId, step, isLoading, plan, quotes }: ConsumerStepContentProps) {
  const Step = CONSUMER_STEP_COMPONENTS[step];

  if (plan.isError) return <LoadError title="We couldn’t load your plan" onRetry={plan.retry} />;
  if (quotes.hasFailed) return <LoadError title="We couldn’t load your insurance options" onRetry={quotes.retry} />;
  if (isLoading) return <ConsumerLoadingState clientId={clientId} />;
  return <Step key={step} clientId={clientId} />;
}

// Screens that start a new policy review; while one is with our team they lead to its status.
const NEW_REVIEW_STEPS: readonly ConsumerStepId[] = ['existing-policy', 'review-upload', 'review-checkout'];

function ConsumerStep({ clientId, step }: { clientId: string; step: ConsumerStepId }) {
  const { isLoading: isClientLoading } = useConsumerClient(clientId);
  const { isLoading: isPlanLoading, isError, refetch, applications } = useConsumerPlan(clientId);
  const quotes = useStepQuotes(clientId, step);
  const reviewInProgress = usePolicyReviewInProgress(clientId);
  useRememberStep(clientId, step);

  // Sending the application ends the flow; every other screen leads back to its confirmation.
  if (applications.length > 0 && step !== 'submitted') {
    return <Navigate to={consumerStepPath(clientId, 'submitted')} replace />;
  }
  if (reviewInProgress && NEW_REVIEW_STEPS.includes(step)) {
    return <Navigate to={consumerStepPath(clientId, 'review-status')} replace />;
  }
  if (!isPlanLoading && !isError && quotes.isChoiceMissing) {
    return <Navigate to={consumerStepPath(clientId, 'options')} replace />;
  }

  return (
    <ConsumerFlowLayout clientId={clientId} step={step}>
      <ConsumerStepContent
        clientId={clientId}
        step={step}
        isLoading={isClientLoading || isPlanLoading || quotes.isWaiting}
        plan={{ isError, retry: () => refetch() }}
        quotes={{ hasFailed: quotes.hasFailed, retry: () => quotes.retry() }}
      />
    </ConsumerFlowLayout>
  );
}

export function ConsumerFlowPage({ clientId, step }: ConsumerFlowPageProps) {
  if (isConsumerStep(step)) return <ConsumerStep clientId={clientId} step={step} />;
  const replacement = currentConsumerStep(step);
  return <Navigate to={replacement ? consumerStepPath(clientId, replacement) : consumerEntryPath(clientId)} replace />;
}
